import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface ManualSidingMeasurementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  tradeId: string;
  orgId: string;
  address?: string;
  onSuccess: () => void;
}

export function ManualSidingMeasurementDialog({
  open,
  onOpenChange,
  jobId,
  tradeId,
  orgId,
  address,
  onSuccess,
}: ManualSidingMeasurementDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    siding_sqft: "",
    soffit_sqft: "",
    fascia_ft: "",
    trim_linear_feet: "",
    window_count: "",
    door_count: "",
    stories: "",
    material_type: "Vinyl",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const insertData: any = {
        job_id: jobId,
        job_trade_id: tradeId,
        org_id: orgId,
        address: address || null,
        created_by: userData.user?.id,
        material_type: formData.material_type,
      };

      if (formData.siding_sqft) insertData.siding_sqft = parseFloat(formData.siding_sqft);
      if (formData.soffit_sqft) insertData.soffit_sqft = parseFloat(formData.soffit_sqft);
      if (formData.fascia_ft) insertData.fascia_ft = parseFloat(formData.fascia_ft);
      if (formData.trim_linear_feet) insertData.trim_linear_feet = parseFloat(formData.trim_linear_feet);
      if (formData.window_count) insertData.window_count = parseInt(formData.window_count);
      if (formData.door_count) insertData.door_count = parseInt(formData.door_count);
      if (formData.stories) insertData.stories = parseInt(formData.stories);

      const { error } = await supabase.from("measurements_siding").insert(insertData);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Siding measurements saved successfully",
      });

      setFormData({
        siding_sqft: "",
        soffit_sqft: "",
        fascia_ft: "",
        trim_linear_feet: "",
        window_count: "",
        door_count: "",
        stories: "",
        material_type: "Vinyl",
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error saving siding measurements:", error);
      toast({
        title: "Error",
        description: "Failed to save siding measurements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enter Siding Measurements</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="siding_sqft">Siding Area (sq ft)</Label>
            <Input
              id="siding_sqft"
              type="number"
              step="0.01"
              placeholder="e.g., 1800"
              value={formData.siding_sqft}
              onChange={(e) => setFormData({ ...formData, siding_sqft: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="soffit_sqft">Soffit (sq ft)</Label>
              <Input
                id="soffit_sqft"
                type="number"
                step="0.01"
                placeholder="e.g., 200"
                value={formData.soffit_sqft}
                onChange={(e) => setFormData({ ...formData, soffit_sqft: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fascia_ft">Fascia (linear ft)</Label>
              <Input
                id="fascia_ft"
                type="number"
                step="0.01"
                placeholder="e.g., 100"
                value={formData.fascia_ft}
                onChange={(e) => setFormData({ ...formData, fascia_ft: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="trim_linear_feet">Trim (linear ft)</Label>
            <Input
              id="trim_linear_feet"
              type="number"
              step="0.01"
              placeholder="e.g., 150"
              value={formData.trim_linear_feet}
              onChange={(e) => setFormData({ ...formData, trim_linear_feet: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="window_count">Windows</Label>
              <Input
                id="window_count"
                type="number"
                placeholder="e.g., 12"
                value={formData.window_count}
                onChange={(e) => setFormData({ ...formData, window_count: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="door_count">Doors</Label>
              <Input
                id="door_count"
                type="number"
                placeholder="e.g., 3"
                value={formData.door_count}
                onChange={(e) => setFormData({ ...formData, door_count: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="stories">Stories</Label>
              <Input
                id="stories"
                type="number"
                placeholder="e.g., 2"
                value={formData.stories}
                onChange={(e) => setFormData({ ...formData, stories: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="material_type">Material Type</Label>
            <Select value={formData.material_type} onValueChange={(value) => setFormData({ ...formData, material_type: value })}>
              <SelectTrigger id="material_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Vinyl">Vinyl</SelectItem>
                <SelectItem value="Fiber Cement">Fiber Cement</SelectItem>
                <SelectItem value="Wood">Wood</SelectItem>
                <SelectItem value="Metal">Metal</SelectItem>
                <SelectItem value="Stucco">Stucco</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Measurements
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
